import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CompleteAction, CompleteType } from 'generated/graphql';

/** How long a mark lasts when its change is never seen to land. */
export const MARK_TTL = 90000;

/** What an action left on an entity: its eTag when the action ran, and how the action completed. */
export interface ETagMark {
  eTag: string | string[];
  onComplete: CompleteAction;
  time: number;
}

export const firstETag = (eTag?: string | string[]) => (Array.isArray(eTag) ? eTag[0] : eTag);

// An eTag reads prefix.id.hash.timestamp, as nache's Tag writes it: the prefix and id name the
// entity, and the hash its version. A list of eTags is keyed by its first.
const parts = (eTag?: string | string[]) => firstETag(eTag)?.split('.') ?? [];

/** The entity an eTag names, which keys its mark; undefined for an eTag that names none. */
export const entityOf = (eTag?: string | string[]) => {
  const [prefix, id] = parts(eTag);
  return prefix && id ? `${prefix}.${id}` : undefined;
};

const version = (eTag?: string | string[]) => parts(eTag).slice(0, 3).join('.');

/** Whether mark was made against the version of the entity that eTag names. */
export const isSameVersion = (mark: ETagMark, eTag?: string | string[]) => version(mark.eTag) === version(eTag);

const isExpired = (mark: ETagMark) => Date.now() - mark.time >= MARK_TTL;

interface ETagStore {
  /** Keyed by entityOf, so every button whose eTag names the same entity shares its mark. */
  marks: Record<string, ETagMark>;
  mark: (eTag: string | string[], onComplete: CompleteAction) => void;
  clear: (eTag: string | string[]) => void;
}

// Clears the mark once its TTL runs out, unless a newer mark has replaced it by then.
const scheduleExpiry = (entity: string, time: number) =>
  setTimeout(
    () => {
      const store = useETagStore.getState();
      const mark = store.marks[entity];
      if (mark?.time === time) store.clear(mark.eTag);
    },
    Math.max(0, time + MARK_TTL - Date.now()),
  );

/**
 * Persisted so an entity keeps its mark across a reload or a visit to another screen, until its
 * change lands or MARK_TTL runs out.
 */
const useETagStore = create<ETagStore>()(
  persist(
    (set) => ({
      marks: {},
      mark: (eTag, onComplete) => {
        const entity = entityOf(eTag);
        if (!entity) return;
        const time = Date.now();
        set((store) => ({ marks: { ...store.marks, [entity]: { eTag, onComplete, time } } }));
        scheduleExpiry(entity, time);
      },
      clear: (eTag) =>
        set((store) => {
          const entity = entityOf(eTag);
          if (!entity || !(entity in store.marks)) return store;
          const marks = { ...store.marks };
          delete marks[entity];
          return { marks };
        }),
    }),
    {
      name: 'etag-marks',
      partialize: (store) => ({ marks: store.marks }),
      merge: (persisted, current) => ({
        ...current,
        marks: Object.fromEntries(
          Object.entries((persisted as Partial<ETagStore> | undefined)?.marks ?? {}).filter(
            ([, mark]) => !isExpired(mark),
          ),
        ),
      }),
      onRehydrateStorage: () => (store) =>
        Object.entries(store?.marks ?? {}).forEach(([entity, { time }]) => scheduleExpiry(entity, time)),
    },
  ),
);

/**
 * Marks an entity as waiting on a change made elsewhere, such as on an edit screen. Its buttons
 * wait on the change as though their own Stale action had made it.
 */
export const markStale = (eTag: string | string[]) =>
  useETagStore.getState().mark(eTag, { type: CompleteType.Stale, nextLabel: '' });

export default useETagStore;
